import { api } from './client';
import type {
  BoardCreateInput,
  BoardUpdateInput,
  CardCreateInput,
  CardUpdateInput,
  CardMoveInput,
  ChecklistItemCreateInput,
  ChecklistItemUpsertInput,
  ChecklistUpsertInput,
  CommentCreateInput,
  LabelUpsertInput,
  ListUpsertInput,
} from '@shared/contracts/kanban';
import type {
  AutomationCreateInput,
  AutomationUpdateInput,
  AutomationTrigger,
  AutomationAction,
} from '@shared/contracts/automation';

export type { AutomationCreateInput, AutomationUpdateInput, AutomationTrigger, AutomationAction };

export type BoardRoleName = 'ADMIN' | 'MEMBER' | 'OBSERVER';

export interface BoardSummaryDto {
  id: string;
  name: string;
  description: string | null;
  archivedAt: string | null;
  isTemplate: boolean;
  createdAt: string;
  updatedAt: string;
  memberCount: number;
  myRole: BoardRoleName | null;
}

export interface BoardMemberDto {
  userId: string;
  role: BoardRoleName;
  displayName: string;
  email: string;
}

export interface LabelDto {
  id: string;
  boardId?: string;
  name: string;
  color: string;
}

export type RecurrenceRule = 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'YEARLY';

export interface CardSummaryDto {
  id: string;
  listId: string;
  title: string;
  position: number;
  startDate: string | null;
  dueDate: string | null;
  completedAt: string | null;
  archived: boolean;
  /** Null = one-off; otherwise the card repeats by this cadence. */
  recurrence: RecurrenceRule | null;
  labelIds: string[];
  memberIds: string[];
  checklistTotal: number;
  checklistDone: number;
  createdAt: string;
  updatedAt: string;
}

export interface CardDetailDto extends CardSummaryDto {
  description: string | null;
  boardId: string;
}

/**
 * Cross-board personal todo row returned by GET /cards/mine. Includes the
 * containing board + list so the UI can group rows without an extra fetch.
 */
export interface MyCardDto {
  id: string;
  title: string;
  startDate: string | null;
  dueDate: string | null;
  completedAt: string | null;
  archived: boolean;
  listId: string;
  listName: string;
  boardId: string;
  boardName: string;
  updatedAt: string;
}

export interface BoardListDto {
  id: string;
  boardId: string;
  name: string;
  position: number;
  archived: boolean;
}

export interface BoardListWithCardsDto extends BoardListDto {
  cards: CardSummaryDto[];
}

export interface BoardDetailDto {
  id: string;
  name: string;
  description: string | null;
  archivedAt: string | null;
  isTemplate: boolean;
  myRole: BoardRoleName | null;
  lists: BoardListWithCardsDto[];
  labels: LabelDto[];
  members: BoardMemberDto[];
  createdAt: string;
  updatedAt: string;
}

export interface ChecklistItemDto {
  id: string;
  checklistId: string;
  text: string;
  completed: boolean;
  position: number;
  dueDate: string | null;
  assignedTo: string | null;
}

export interface ChecklistDto {
  id: string;
  cardId: string;
  title: string;
  position: number;
  items: ChecklistItemDto[];
}

export interface CommentDto {
  id: string;
  cardId: string;
  authorId: string | null;
  body: string;
  createdAt: string;
  updatedAt: string;
}

export interface ActivityDto {
  id: string;
  type: string;
  actorId: string | null;
  payload: unknown;
  createdAt: string;
}

export interface AutomationDto {
  id: string;
  boardId: string;
  name: string;
  enabled: boolean;
  trigger: AutomationTrigger;
  actions: AutomationAction[];
  createdAt: string;
}

export interface CardDependencyEdgeDto {
  id: string;
  blockedById: string;
  blocker: { id: string; title: string; completedAt: string | null };
}

export interface BlockedDownstreamDto {
  id: string;
  cardId: string;
  blocked: { id: string; title: string; completedAt: string | null };
}

export interface CardDependenciesDto {
  blockedBy: CardDependencyEdgeDto[];
  blocks: BlockedDownstreamDto[];
}

export interface BoardTemplateDto {
  id: string;
  name: string;
  description: string | null;
}

export const kanbanApi = {
  // Boards
  listBoards: () => api<{ items: BoardSummaryDto[] }>('/boards'),
  getBoard: (id: string) => api<BoardDetailDto>(`/boards/${id}`),
  createBoard: (body: BoardCreateInput) =>
    api<BoardSummaryDto>('/boards', { method: 'POST', body }),
  updateBoard: (id: string, body: BoardUpdateInput) =>
    api<BoardSummaryDto>(`/boards/${id}`, { method: 'PATCH', body }),
  archiveBoard: (id: string) => api<void>(`/boards/${id}`, { method: 'DELETE' }),
  unarchiveBoard: (id: string) =>
    api<void>(`/boards/${id}/unarchive`, { method: 'POST' }),
  deleteBoardPermanent: (id: string) =>
    api<void>(`/boards/${id}/permanent`, { method: 'DELETE' }),

  // Membership
  addMember: (
    boardId: string,
    userId: string,
    role: BoardRoleName = 'MEMBER',
  ) =>
    api<BoardMemberDto>(`/boards/${boardId}/members`, {
      method: 'POST',
      body: { userId, role },
    }),
  removeMember: (boardId: string, userId: string) =>
    api<void>(`/boards/${boardId}/members/${userId}`, { method: 'DELETE' }),

  // Lists
  createList: (boardId: string, body: ListUpsertInput) =>
    api<BoardListDto>(`/boards/${boardId}/lists`, { method: 'POST', body }),
  updateList: (
    listId: string,
    body: { name?: string; archived?: boolean; position?: number },
  ) => api<BoardListDto>(`/lists/${listId}`, { method: 'PATCH', body }),
  reorderList: (listId: string, position: number) =>
    api<{ items: BoardListDto[] }>(`/lists/${listId}/reorder`, {
      method: 'POST',
      body: { position },
    }),
  archiveList: (listId: string) =>
    api<void>(`/lists/${listId}`, { method: 'DELETE' }),
  /**
   * Bulk-sort every non-archived card in a list. Persists positions to DB
   * so the new order survives reload.
   */
  sortListCards: (
    listId: string,
    body: { field: 'due' | 'name' | 'created'; direction?: 'asc' | 'desc' },
  ) =>
    api<{ items: { id: string; position: number }[] }>(
      `/lists/${listId}/sort-cards`,
      { method: 'POST', body },
    ),

  // Cards
  /**
   * Cross-board "assigned to me" listing. Pass includeCompleted=true to
   * surface cards already marked complete (otherwise they're hidden).
   */
  listMyCards: (params?: { includeCompleted?: boolean }) =>
    api<{ items: MyCardDto[] }>('/cards/mine', {
      query: { includeCompleted: params?.includeCompleted ? 'true' : 'false' },
    }),
  createCard: (listId: string, body: CardCreateInput) =>
    api<CardDetailDto>(`/lists/${listId}/cards`, { method: 'POST', body }),
  getCard: (id: string) => api<CardDetailDto>(`/cards/${id}`),
  updateCard: (id: string, body: CardUpdateInput) =>
    api<CardDetailDto>(`/cards/${id}`, { method: 'PATCH', body }),
  deleteCard: (id: string) => api<void>(`/cards/${id}`, { method: 'DELETE' }),
  moveCard: (id: string, body: CardMoveInput) =>
    api<CardDetailDto>(`/cards/${id}/move`, { method: 'POST', body }),
  /**
   * Duplicate a card into a list on any board the caller can write to.
   * Defaults follow Trello's copy semantics — members + labels + checklists
   * carry across; comments and attachments stay with the original.
   */
  copyCard: (
    id: string,
    body: {
      destListId: string;
      title?: string;
      position?: number;
      includeMembers?: boolean;
      includeLabels?: boolean;
      includeChecklists?: boolean;
      includeComments?: boolean;
      includeAttachments?: boolean;
    },
  ) => api<CardDetailDto>(`/cards/${id}/copy`, { method: 'POST', body }),
  /**
   * Move a card to a list on a *different* board. Labels are remapped by
   * name+colour against the destination; assignees not on the destination
   * board are dropped; dependencies are cleared.
   */
  moveCardToBoard: (id: string, body: { destListId: string; position?: number }) =>
    api<CardDetailDto>(`/cards/${id}/move-to-board`, { method: 'POST', body }),

  // Card labels
  attachLabel: (cardId: string, labelId: string) =>
    api<void>(`/cards/${cardId}/labels`, {
      method: 'POST',
      body: { labelId },
    }),
  detachLabel: (cardId: string, labelId: string) =>
    api<void>(`/cards/${cardId}/labels/${labelId}`, { method: 'DELETE' }),

  // Board labels
  listLabels: (boardId: string) =>
    api<{ items: LabelDto[] }>(`/boards/${boardId}/labels`),
  createLabel: (boardId: string, body: LabelUpsertInput) =>
    api<LabelDto>(`/boards/${boardId}/labels`, { method: 'POST', body }),
  updateLabel: (id: string, body: LabelUpsertInput) =>
    api<LabelDto>(`/labels/${id}`, { method: 'PATCH', body }),
  deleteLabel: (id: string) => api<void>(`/labels/${id}`, { method: 'DELETE' }),

  // Checklists
  listChecklists: (cardId: string) =>
    api<{ items: ChecklistDto[] }>(`/cards/${cardId}/checklists`),
  createChecklist: (cardId: string, body: ChecklistUpsertInput) =>
    api<ChecklistDto>(`/cards/${cardId}/checklists`, { method: 'POST', body }),
  updateChecklist: (id: string, body: ChecklistUpsertInput) =>
    api<ChecklistDto>(`/checklists/${id}`, { method: 'PATCH', body }),
  deleteChecklist: (id: string) =>
    api<void>(`/checklists/${id}`, { method: 'DELETE' }),
  createChecklistItem: (checklistId: string, body: ChecklistItemCreateInput) =>
    api<ChecklistItemDto>(`/checklists/${checklistId}/items`, {
      method: 'POST',
      body,
    }),
  updateChecklistItem: (itemId: string, body: ChecklistItemUpsertInput) =>
    api<ChecklistItemDto>(`/checklist-items/${itemId}`, {
      method: 'PATCH',
      body,
    }),
  deleteChecklistItem: (itemId: string) =>
    api<void>(`/checklist-items/${itemId}`, { method: 'DELETE' }),

  // Comments
  listComments: (cardId: string) =>
    api<{ items: CommentDto[] }>(`/cards/${cardId}/comments`),
  createComment: (cardId: string, body: CommentCreateInput) =>
    api<CommentDto>(`/cards/${cardId}/comments`, { method: 'POST', body }),

  // Activities
  boardActivities: (boardId: string, limit = 100) =>
    api<{ items: ActivityDto[] }>(`/boards/${boardId}/activities`, {
      query: { limit },
    }),
  cardActivities: (cardId: string, limit = 100) =>
    api<{ items: ActivityDto[] }>(`/cards/${cardId}/activities`, {
      query: { limit },
    }),

  // Automations (Butler)
  listAutomations: (boardId: string) =>
    api<{ items: AutomationDto[] }>(`/boards/${boardId}/automations`),
  createAutomation: (boardId: string, body: AutomationCreateInput) =>
    api<AutomationDto>(`/boards/${boardId}/automations`, {
      method: 'POST',
      body,
    }),
  updateAutomation: (id: string, body: AutomationUpdateInput) =>
    api<AutomationDto>(`/automations/${id}`, { method: 'PATCH', body }),
  deleteAutomation: (id: string) =>
    api<void>(`/automations/${id}`, { method: 'DELETE' }),

  // Card dependencies
  listDependencies: (cardId: string) =>
    api<CardDependenciesDto>(`/cards/${cardId}/dependencies`),
  addDependency: (cardId: string, blockedById: string) =>
    api<{ id: string; cardId: string; blockedById: string; createdAt: string }>(
      `/cards/${cardId}/dependencies`,
      { method: 'POST', body: { blockedById } },
    ),
  removeDependency: (cardId: string, blockerId: string) =>
    api<void>(`/cards/${cardId}/dependencies/${blockerId}`, {
      method: 'DELETE',
    }),

  // Board templates
  listTemplates: () => api<{ items: BoardTemplateDto[] }>('/board-templates'),
  instantiateTemplate: (
    templateId: string,
    body: { name: string; description?: string | null },
  ) =>
    api<{ boardId: string }>(`/board-templates/${templateId}/instantiate`, {
      method: 'POST',
      body,
    }),
  setTemplateFlag: (boardId: string, isTemplate: boolean) =>
    api<void>(`/boards/${boardId}/template-flag`, {
      method: 'POST',
      body: { isTemplate },
    }),
};
